import StageOrientation from '../StageOrientation';
import { SyosNonSeat } from '../SyosSeatMap';
import SeatDataBuilder from './SeatDataBuilder';
import { GetSeatListResponse, SyosSeatData, Zone } from './types';
import SeatIconBuilder from '../seatIcons/SeatIconBuilder';

export interface ProcessSeatsData {
  getSeatListResponse: GetSeatListResponse;
  zones: Map<number, Zone>;
}

interface ProcessSeatsResult {
  seats: SyosSeatData[];
  nonSeats: SyosNonSeat[];
  stageOrientation: string;
  rowCount: number;
  columnCount: number;
}

function processSeats({
  getSeatListResponse,
  zones,
}: ProcessSeatsData): ProcessSeatsResult {
  const seatDataBuilder = new SeatDataBuilder(SeatIconBuilder);

  getSeatListResponse.ZoneColorList.forEach((zoneColor) => {
    const zone = zones.get(zoneColor.zone_no)!;

    // Mutate zone objects
    zone.color = zoneColor.zone_color;
    zone.iconCharacter = zoneColor.IconCharacter;
  });

  const { nonSeats, stageOrientation } = getSeatListResponse;

  const seats = getSeatListResponse.seats
    .map((seatApiData) => seatDataBuilder.build(seatApiData, getSeatListResponse.sections, zones));

  const { rowCount, columnCount } = mutateSeatCoordinates(seats, nonSeats, stageOrientation);

  return {
    seats,
    nonSeats,
    stageOrientation,
    rowCount,
    columnCount,
  };
}

function mutateSeatCoordinates(seats: SyosSeatData[], nonSeats: SyosNonSeat[], stageOrientation: string) {
  let minX = Number.MAX_SAFE_INTEGER;
  let minY = Number.MAX_SAFE_INTEGER;
  let maxX = -Number.MAX_SAFE_INTEGER;
  let maxY = -Number.MAX_SAFE_INTEGER;

  const allSeats = [...seats, ...nonSeats];

  allSeats.forEach((seat) => {
    minX = Math.min(minX, seat.x);
    minY = Math.min(minY, seat.y);
    maxX = Math.max(maxX, seat.x);
    maxY = Math.max(maxY, seat.y);
  });

  // How much in the x or y axis the stage offsets seat coordinates:
  const STAGE_GRID_OFFSET = 3;
  let stageOffsetX = 0;
  let stageOffsetY = 0;

  switch (stageOrientation) {
    case StageOrientation.LEFT:
      stageOffsetX = STAGE_GRID_OFFSET;
      break;
    case StageOrientation.TOP:
      stageOffsetY = STAGE_GRID_OFFSET;
      break;
    case StageOrientation.RIGHT:
      maxX += STAGE_GRID_OFFSET;
      break;
    case StageOrientation.BOTTOM:
      maxY += STAGE_GRID_OFFSET;
      break;
    default:
      break;
  }

  if (minX || minY || stageOffsetX || stageOffsetY) {
    allSeats.forEach((seat) => {
      seat.x = (seat.x - minX) + stageOffsetX;
      seat.y = (seat.y - minY) + stageOffsetY;
    });

    maxX -= minX;
    maxY -= minY;
  }

  return {
    columnCount: maxX + stageOffsetX + 1,
    rowCount: maxY + stageOffsetY + 1,
  };
}

export default processSeats;
